import { components, OptionProps } from "react-select";

import {
  FormAsyncDropdown,
  SingleValueProps,
  Stack,
  Text
} from "@seneca/senekit";

import {
  fetchSchoolsDropdownOptions,
  SchoolOption
} from "seneca-common/features/schools/service";

type Props = {
  name: string;
  value: SchoolOption;
  isDisabled?: boolean;
  error?: any;
  showError?: boolean;
  onChange: (option: SchoolOption) => void;
  onBlur: () => void;
};

export default function SchoolsDropdownComponent({
  name,
  value,
  isDisabled,
  error,
  showError,
  onChange,
  onBlur
}: Props) {
  return (
    <FormAsyncDropdown
      name={name}
      label="School"
      value={value}
      loadOptions={fetchSchoolsDropdownOptions}
      onChange={onChange as any}
      onBlur={onBlur}
      showError={showError}
      error={error}
      placeholder={<>Type your school name...</>}
      noOptionsMessage={NoOptionsMessage}
      isDisabled={isDisabled}
      cacheOptions
      openMenuOnFocus
      components={{ SingleValue, Option }}
    />
  );
}

function SingleValue(props: SingleValueProps<SchoolOption, false>) {
  const { data } = props;

  if (data.cantFindOption) {
    return (
      <components.SingleValue {...props}>
        <Text>🤔 Can't find my school</Text>
      </components.SingleValue>
    );
  }

  const { name } = data;

  return (
    <components.SingleValue {...props}>
      <Text dataNoTranslate>{name}</Text>
    </components.SingleValue>
  );
}

function Option(props: OptionProps<SchoolOption, false>) {
  const { data } = props;

  if (data.cantFindOption) {
    return (
      <components.Option {...props}>
        <Stack>
          <Text>🤔 Can't find your school?</Text>
        </Stack>
      </components.Option>
    );
  }

  const { name, localAuthority, town, postcode } = data;

  const location =
    town && localAuthority ? (
      <>
        {town}, {localAuthority}
      </>
    ) : localAuthority ? (
      <>{localAuthority}</>
    ) : null;

  return (
    <components.Option {...props}>
      <Stack>
        <Text dataNoTranslate>{name}</Text>
        {location && (
          <Text tone="neutral" size="small" dataNoTranslate>
            {location}
            {postcode && `, ${postcode}`}
          </Text>
        )}
      </Stack>
    </components.Option>
  );
}

function NoOptionsMessage({ inputValue }: { inputValue: string }) {
  return inputValue.length === 0 ? (
    <>Type your school name...</>
  ) : inputValue.length < 3 ? (
    <>Keep typing...</>
  ) : (
    <>Sorry, we couldn't find your school!</>
  );
}
